import { Clock, Lock, Search as SearchIcon, X } from 'lucide-react';
import { type ReactNode, useEffect, useId, useState } from 'react';
import { Link, useNavigate, useSearchParams } from 'react-router';
import { initials } from '../components/community/Avatar';
import { AppFrame } from '../components/frame/AppFrame';
import { Alert } from '../components/ui';
import { ApiError, apiFetch } from '../lib/api';
import { type SearchResults, search } from '../lib/communities';

const RECENT_KEY = 'mogzo.recent-searches';
const RECENT_MAX = 5;

function readRecent(): string[] {
  try {
    const saved = JSON.parse(localStorage.getItem(RECENT_KEY) ?? '[]');
    return Array.isArray(saved) ? saved.filter((q) => typeof q === 'string') : [];
  } catch {
    return [];
  }
}

function remember(q: string) {
  try {
    const next = [q, ...readRecent().filter((r) => r !== q)].slice(0, RECENT_MAX);
    localStorage.setItem(RECENT_KEY, JSON.stringify(next));
  } catch {
    // Private windows can refuse storage; recent searches are only a convenience.
  }
}

/** The name with the part that matched underlined in yellow. */
function Highlight({ text, q }: { text: string; q: string }) {
  const at = text.toLowerCase().indexOf(q.toLowerCase());
  if (at < 0 || !q) return <>{text}</>;
  return (
    <>
      {text.slice(0, at)}
      <span className="font-bold underline decoration-brand decoration-[3px] underline-offset-[3px]">
        {text.slice(at, at + q.length)}
      </span>
      {text.slice(at + q.length)}
    </>
  );
}

function Heading({ children }: { children: ReactNode }) {
  return (
    <h2 className="mx-1 mt-3.5 mb-1.5 text-xs font-semibold tracking-[0.06em] text-muted uppercase">
      {children}
    </h2>
  );
}

type Found =
  | { status: 'idle' }
  | { status: 'loading' }
  | { status: 'closed' }
  | { status: 'failed' }
  | { status: 'ready'; q: string; results: SearchResults };

/**
 * mogzo.com/search (M28): games and communities by name. While coming soon is on, only staff
 * find games and public communities; everyone finds the private communities they're in, and
 * nothing else, as if the rest didn't exist.
 */
export function Search() {
  const [params, setParams] = useSearchParams();
  const navigate = useNavigate();
  const q = params.get('q')?.trim() ?? '';
  const [text, setText] = useState(params.get('q') ?? '');
  const [found, setFound] = useState<Found>({ status: 'idle' });
  const [publicSide, setPublicSide] = useState<boolean | null>(null);
  const [recent, setRecent] = useState(readRecent);
  const fieldId = useId();

  useEffect(() => {
    apiFetch<{ seesPublicSide: boolean }>('/v1/site')
      .then((site) => setPublicSide(site.seesPublicSide))
      .catch(() => setPublicSide(null));
  }, []);

  // The address follows the field, a moment after typing stops.
  useEffect(() => {
    const next = text.trim();
    if (next === q) return;
    const timer = setTimeout(() => setParams(next ? { q: next } : {}, { replace: true }), 250);
    return () => clearTimeout(timer);
  }, [text, q, setParams]);

  useEffect(() => {
    if (!q) {
      setFound({ status: 'idle' });
      return;
    }
    const controller = new AbortController();
    setFound({ status: 'loading' });
    search(q, controller.signal)
      .then((results) => {
        setFound({ status: 'ready', q, results });
        if (results.games.length || results.communities.length) {
          remember(q);
          setRecent(readRecent());
        }
      })
      .catch((error) => {
        if (controller.signal.aborted) return;
        setFound({
          status: error instanceof ApiError && error.status === 404 ? 'closed' : 'failed',
        });
      });
    return () => controller.abort();
  }, [q]);

  const field = (
    <search className="min-w-0 flex-1">
      <form
        onSubmit={(event) => {
          event.preventDefault();
          setParams(text.trim() ? { q: text.trim() } : {}, { replace: true });
        }}
      >
        <label htmlFor={fieldId} className="sr-only">
          Search games and communities
        </label>
        <div className="relative">
          <SearchIcon
            size={20}
            strokeWidth={1.9}
            aria-hidden="true"
            className="pointer-events-none absolute top-3 left-3.5 text-muted"
          />
          <input
            id={fieldId}
            type="search"
            autoComplete="off"
            enterKeyHint="search"
            placeholder="Search games and communities"
            value={text}
            onChange={(event) => setText(event.target.value)}
            className="h-11 w-full rounded-full border border-transparent bg-field pr-10 pl-[42px] text-[15px] placeholder:text-muted focus:border-ink focus:bg-card focus:shadow-[0_0_0_4px_var(--color-cream)] focus:outline-none [&::-webkit-search-cancel-button]:hidden"
          />
          {text && (
            <button
              type="button"
              aria-label="Clear"
              onClick={() => setText('')}
              className="absolute top-1.5 right-1.5 flex size-8 items-center justify-center rounded-full text-muted hover:text-ink"
            >
              <X size={16} strokeWidth={2} aria-hidden="true" />
            </button>
          )}
        </div>
      </form>
    </search>
  );

  const comingSoonNote = publicSide === false && (
    <p className="mx-1.5 mt-3.5 text-[13px] leading-[19px] text-muted">
      Games and public communities show here from the launch.
    </p>
  );

  return (
    <AppFrame
      search={<div className="flex w-full max-w-[560px]">{field}</div>}
      header={
        <>
          {field}
          <button
            type="button"
            onClick={() => navigate(-1)}
            className="h-10 px-2.5 text-sm font-semibold text-muted hover:text-ink"
          >
            Cancel
          </button>
        </>
      }
    >
      <main aria-label="Search" className="mx-auto w-full max-w-[700px] px-3 pt-1.5 pb-10 lg:pt-6">
        {found.status === 'idle' && (
          <>
            {comingSoonNote}
            {recent.length > 0 && (
              <>
                <Heading>Recent searches</Heading>
                <ul>
                  {recent.map((r) => (
                    <li key={r}>
                      <button
                        type="button"
                        onClick={() => setText(r)}
                        className="flex min-h-12 w-full items-center gap-3 rounded-xl px-1 text-left hover:bg-field"
                      >
                        <Clock
                          size={18}
                          strokeWidth={1.9}
                          aria-hidden="true"
                          className="text-muted"
                        />
                        <span className="font-medium">{r}</span>
                      </button>
                    </li>
                  ))}
                </ul>
              </>
            )}
          </>
        )}

        {found.status === 'loading' && (
          <p role="status" className="py-8 text-center text-sm text-muted">
            Searching…
          </p>
        )}
        {found.status === 'failed' && (
          <div className="mt-3">
            <Alert>Couldn’t search. Check your connection and try again.</Alert>
          </div>
        )}
        {found.status === 'closed' && (
          <div className="px-5 pt-16 text-center">
            <h1 className="text-lg font-semibold">Nothing to find yet</h1>
            <p className="mt-1.5 leading-[21px] text-muted">
              MOGZO is coming soon.{' '}
              <Link to="/sign-in?next=/search" className="font-semibold text-ink underline">
                Sign in
              </Link>{' '}
              to find the communities you’re in.
            </p>
          </div>
        )}

        {found.status === 'ready' && (
          <div aria-live="polite">
            {found.results.games.length === 0 && found.results.communities.length === 0 ? (
              <div className="px-5 pt-14 text-center">
                <span
                  aria-hidden="true"
                  className="inline-flex size-16 items-center justify-center rounded-full bg-field text-muted"
                >
                  <SearchIcon size={24} strokeWidth={1.9} />
                </span>
                <h1 className="mt-4 text-lg font-semibold">No results for “{found.q}”</h1>
                <p className="mt-1.5 leading-[21px] text-muted">
                  Check the spelling, or try another name.
                </p>
              </div>
            ) : (
              <>
                <h1 className="sr-only">Results for “{found.q}”</h1>
                {found.results.communities.length > 0 && (
                  <>
                    <Heading>Communities</Heading>
                    <ul className="flex flex-col gap-2">
                      {found.results.communities.map((community) => (
                        <li key={community.id}>
                          <Link
                            to={community.path}
                            className="flex items-center gap-3 rounded-[14px] border border-line bg-card p-2 hover:border-muted"
                          >
                            <span
                              aria-hidden="true"
                              className="flex size-12 shrink-0 items-center justify-center rounded-[13px] bg-brand font-display text-[17px] font-bold text-ink"
                            >
                              {initials(community.name)}
                            </span>
                            <span className="min-w-0 flex-1 leading-[18px]">
                              <b className="block truncate font-semibold">
                                <Highlight text={community.name} q={found.q} />
                              </b>
                              <span className="text-[13px] text-muted">
                                {community.memberCount} member
                                {community.memberCount === 1 ? '' : 's'}
                                {community.isMember ? ' · you’re a member' : ''}
                              </span>
                            </span>
                            {community.visibility === 'private' ? (
                              <span className="inline-flex h-6 items-center gap-1.5 rounded-full border border-[#f3dfb5] bg-cream px-2.5 text-xs font-semibold">
                                <Lock size={12} strokeWidth={2.4} aria-hidden="true" />
                                Private
                              </span>
                            ) : (
                              <span className="inline-flex h-6 items-center rounded-full border border-line bg-field px-2.5 text-xs font-semibold">
                                Public
                              </span>
                            )}
                          </Link>
                        </li>
                      ))}
                    </ul>
                  </>
                )}
                {found.results.games.length > 0 && (
                  <>
                    <Heading>Games</Heading>
                    <ul className="flex flex-col gap-2">
                      {found.results.games.map((game) => (
                        <li key={game.slug}>
                          <Link
                            to={game.path}
                            className="flex items-center gap-3 rounded-[14px] border border-line bg-card p-2 hover:border-muted"
                          >
                            <span
                              aria-hidden="true"
                              className="flex size-12 shrink-0 items-center justify-center rounded-[13px] bg-ink font-display text-[17px] font-bold text-brand"
                            >
                              {initials(game.name)}
                            </span>
                            <b className="min-w-0 flex-1 truncate font-semibold">
                              <Highlight text={game.name} q={found.q} />
                            </b>
                          </Link>
                        </li>
                      ))}
                    </ul>
                  </>
                )}
                {comingSoonNote}
              </>
            )}
          </div>
        )}
      </main>
    </AppFrame>
  );
}
